import type { ReactNode } from "react";

type Tone = "teal" | "neutral";

interface CodeBadgeProps {
  children: ReactNode;
  tone?: Tone;
  /**
   * Render the value in its own case instead of upper-casing it.
   *
   * Pass this for anything CASE-SENSITIVE — a cuid, a token, a slug. Upper-casing
   * a store id turns `cmsrbxqnw0001hf5sipr7ppyv` into `CMSRBXQNW0001HF5SIPR7PPYV`,
   * where an admin reading it off the screen cannot tell `I` from `l` or `5` from
   * `S`, and types an id that does not exist. Plan codes and status strings are
   * genuinely upper-case, so that stays the default.
   *
   * An opt-in prop rather than a `normal-case` class from the call site: both are
   * `text-transform` utilities, so which one wins depends on Tailwind's emission
   * order rather than on anything visible in the JSX.
   */
  preserveCase?: boolean;
  className?: string;
}

const TONE_CLASSES: Record<Tone, string> = {
  teal:
    "bg-teal-50 text-teal-700 ring-teal-600/20 " +
    "dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/25",
  neutral:
    "bg-gray-50 text-secondary ring-gray-300/70 " +
    "dark:bg-gray-800/60 dark:text-primary dark:ring-gray-700",
};

const DOT_CLASSES: Record<Tone, string> = {
  teal: "bg-teal-600 dark:bg-teal-400",
  neutral: "bg-gray-400 dark:bg-gray-500",
};

export default function CodeBadge({
  children,
  tone = "teal",
  preserveCase = false,
  className = "",
}: CodeBadgeProps) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5",
        "px-2 py-0.5 rounded-md",
        "ring-1 ring-inset",
        "font-mono text-2xs font-semibold tracking-wider",
        preserveCase ? "normal-case" : "uppercase",
        "leading-5 whitespace-nowrap",
        TONE_CLASSES[tone],
        className,
      ].join(" ")}
    >
      <span
        className={`w-1 h-1 rounded-full ${DOT_CLASSES[tone]}`}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}
